/**
 * Het tabblad Spraakproef (stap 28): kan Voxtral (Mistral) horen welk woord
 * iemand in een zin met een gat zegt? Een proef vóór de woordenoefening.
 *
 * Kies een woord, zeg de hele zin hardop met een woord in het gat, en zie wat
 * het model ervan maakt, in vijf condities (backend: services/spraak.py):
 *   zonder biasing        het model krijgt alleen de opname
 *   het doelwoord         het model krijgt het woord dat we verwachten mee
 *   de hele set           het model krijgt alle woorden van de proef mee
 *   set + foute woorden   en de aannemelijke foute woorden (tas, snor ...)
 *   + de zin              en een beschrijving van de zin, met de taal
 * Vul bij "Ik zeg" in wat je echt in het gat zegt: het doelwoord, een ander
 * woord ("tas" bij de koffer), of iets onduidelijks. De uitkomst is goed,
 * bijna (het klinkt als het doelwoord: "baan" bij "baard"), een ander woord,
 * of niet verstaan. De samenvatting telt hoe vaak het doelwoord herkend werd
 * (goed of bijna) en hoe vaak een ander woord toch goed of bijna heette: de
 * prijs van biasing. Oude opnames rekent de server met de regels van nu door.
 *
 * De opname gaat als WAV naar de server (components/wavOpname.js) en wordt
 * daar niet bewaard, alleen wat het model ervan maakte.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { Draaier, Laden } from '../../../components/Laden';
import { begin as beginOpname } from '../../../components/wavOpname';

const NAMEN = {
  geen: 'Zonder biasing', woord: 'Met het doelwoord', set: 'Met de hele set',
  set_ruim: 'Set + foute woorden', zin: 'Set + foute woorden + de zin',
};
const UITKOMST = { goed: 'goed', bijna: 'bijna', ander: 'ander woord', niet_verstaan: 'niet verstaan' };
const KLEUR = { goed: 'var(--goed-2)', bijna: 'var(--petrol)', ander: 'var(--fout)', niet_verstaan: 'var(--tekst-3)' };
const label = (u) => (u.fout ? '-' : <span style={{ color: KLEUR[u.uitkomst], fontWeight: 600 }}>{UITKOMST[u.uitkomst] || ''}</span>);
const MAX_SECONDEN = 8;
const procent = (deel, geheel) => (geheel ? `${Math.round((100 * deel) / geheel)}%` : '-');

export function Spraakproef({ actief, meldFout }) {
  const [gegevens, setGegevens] = useState(null);
  const [keuze, setKeuze] = useState(0);
  const [vormen, setVormen] = useState('');
  const [bedoeld, setBedoeld] = useState('');
  const [model, setModel] = useState('');
  const [spreker, setSpreker] = useState('');
  const [neemtOp, setNeemtOp] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [laatste, setLaatste] = useState(null);
  const [zekerWissen, setZekerWissen] = useState(false);
  const opname = useRef(null);
  const wekker = useRef(null);

  const kies = (i, lijst = gegevens.woorden) => {
    setKeuze(i);
    setVormen(lijst[i].goede_vormen.join(', '));
    setBedoeld(lijst[i].woord);
    setLaatste(null);
  };

  useEffect(() => {
    if (!actief) return;
    api.haalSpraakproef().then((data) => {
      setGegevens(data);
      setModel(m => m || data.modellen[0]);
      if (!bedoeld) kies(0, data.woorden);
    }).catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  useEffect(() => () => clearTimeout(wekker.current), []);

  if (!gegevens) return <Laden tekst="Spraakproef laden..." />;
  const woord = gegevens.woorden[keuze];

  const stop = async () => {
    clearTimeout(wekker.current);
    const lopend = opname.current;
    opname.current = null;
    setNeemtOp(false);
    if (!lopend) return;
    setBezig(true);
    try {
      const wav = await lopend.stop();
      const data = await api.spreekIn({ opname: wav, woord: woord.woord, zin: woord.zin, goedeVormen: vormen,
        bedoeld: bedoeld || woord.woord, model, spreker });
      setLaatste({ bedoeld: bedoeld || woord.woord, uitslag: data.uitslag });
      setGegevens(data);
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBezig(false);
    }
  };

  const start = async () => {
    setLaatste(null);
    try {
      opname.current = await beginOpname();
      setNeemtOp(true);
      wekker.current = setTimeout(stop, MAX_SECONDEN * 1000);
    } catch (err) {
      meldFout(`De microfoon is niet beschikbaar: ${err.message}. Geef de browser toestemming om de microfoon te gebruiken.`);
    }
  };

  const wis = async () => {
    if (!zekerWissen) { setZekerWissen(true); return; }
    setZekerWissen(false);
    try {
      setGegevens(await api.wisSpraakproef());
      setLaatste(null);
    } catch (err) {
      meldFout(err.message);
    }
  };

  // de uitslagen per opname, de nieuwste eerst
  const opnames = [];
  gegevens.uitslagen.forEach((u) => {
    let o = opnames.find(x => x.opname === u.opname);
    if (!o) { o = { ...u, condities: {} }; opnames.push(o); }
    o.condities[u.conditie] = u;
  });

  return (
    <div className="card">
      <h2>Spraakproef</h2>
      <p className="text-muted">
        Kies een woord en zeg de hele zin hardop, met een woord in het gat. Vul bij <em>Ik zeg</em> in wat je echt
        zegt: het doelwoord, een ander woord (zoals <em>tas</em> bij de koffer) of iets onduidelijks. Elke opname gaat vijf
        keer naar het model: zonder biasing, met het doelwoord, met alle woorden van de proef, met die woorden plus de
        foute woorden, en met dat alles plus de zin. <em>Bijna</em> betekent: het klinkt als het doelwoord (zoals
        <em>baan</em> bij <em>baard</em>); dat telt als herkend. Oude opnames worden met de regels van nu doorgerekend.
        De opname zelf wordt niet bewaard.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
        <div className="form-group">
          <label htmlFor="sp-woord">Woord</label>
          <select id="sp-woord" className="input" value={keuze} onChange={e => kies(Number(e.target.value))}>
            {gegevens.woorden.map((w, i) => <option key={w.woord} value={i}>{w.woord}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="sp-vormen">Goede vormen (met komma's)</label>
          <input id="sp-vormen" className="input" value={vormen} onChange={e => setVormen(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="sp-bedoeld">Ik zeg</label>
          <input id="sp-bedoeld" className="input" value={bedoeld} onChange={e => setBedoeld(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="sp-model">Model</label>
          <select id="sp-model" className="input" value={model} onChange={e => setModel(e.target.value)}>
            {gegevens.modellen.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="sp-spreker">Spreker (naam of omschrijving)</label>
          <input id="sp-spreker" className="input" value={spreker} onChange={e => setSpreker(e.target.value)} placeholder="bijv. volwassene, NT2" />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, margin: '16px 0', flexWrap: 'wrap' }}>
        <p data-testid="spraak-zin" style={{ fontSize: 24, margin: 0 }}>
          {woord.zin.split('___').map((stuk, i, alle) => (
            <React.Fragment key={i}>{stuk}{i < alle.length - 1 && <span style={{ color: 'var(--accent)' }}>____</span>}</React.Fragment>
          ))}
        </p>
        {neemtOp ? (
          <button className="btn btn-danger" onClick={stop}><Square size={16} /> Stop</button>
        ) : (
          <button className="btn btn-primary" onClick={start} disabled={bezig}>
            {bezig ? <><Draaier grootte={16} /> Het model luistert...</> : <><Mic size={16} /> Opnemen</>}
          </button>
        )}
        {neemtOp && <span className="text-muted">Zeg nu de hele zin; stopt vanzelf na {MAX_SECONDEN} seconden.</span>}
      </div>

      {laatste && (
        <div className="mb-16" data-testid="spraak-uitslag">
          <h3>Deze opname (je zei: {laatste.bedoeld})</h3>
          <table>
            <thead><tr><th>Conditie</th><th>Wat het model hoorde</th><th>In het gat</th><th>Uitkomst</th></tr></thead>
            <tbody>
              {laatste.uitslag.map(u => (
                <tr key={u.conditie}>
                  <td>{NAMEN[u.conditie]}</td>
                  <td>{u.fout ? <span style={{ color: 'var(--fout)' }}>fout: {u.fout}</span> : u.transcript}</td>
                  <td><strong>{u.gezegd}</strong></td>
                  <td>{label(u)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="mt-16">Samenvatting</h3>
      <table data-testid="spraak-samenvatting">
        <thead>
          <tr><th>Conditie</th><th>Doelwoord gezegd</th><th>Goed</th><th>Bijna</th><th>Herkend (goed of bijna)</th>
            <th>Niet verstaan</th><th>Ander woord gezegd</th><th>Toch goed of bijna</th></tr>
        </thead>
        <tbody>
          {gegevens.samenvatting.map(s => (
            <tr key={s.conditie}>
              <td>{NAMEN[s.conditie]}</td>
              <td>{s.doelwoord_gezegd}</td>
              <td>{s.treffers}</td>
              <td>{s.bijna}</td>
              <td><strong>{s.treffers + s.bijna} ({procent(s.treffers + s.bijna, s.doelwoord_gezegd)})</strong></td>
              <td>{s.niet_verstaan}</td>
              <td>{s.ander_woord_gezegd}</td>
              <td><strong>{s.onterecht_goed + s.onterecht_bijna} ({procent(s.onterecht_goed + s.onterecht_bijna, s.ander_woord_gezegd)})</strong></td>
            </tr>
          ))}
        </tbody>
      </table>

      {opnames.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
            <h3 style={{ margin: 0 }}>Alle opnames ({opnames.length})</h3>
            <button className="btn btn-secondary" onClick={wis}>
              <Trash2 size={16} /> {zekerWissen ? 'Zeker weten? Klik nog eens' : 'Uitslagen wissen'}
            </button>
          </div>
          <table className="mt-8">
            <thead>
              <tr><th>Woord</th><th>Gezegd</th><th>Spreker</th><th>Model</th>
                {gegevens.condities.map(c => <th key={c}>{NAMEN[c]}</th>)}</tr>
            </thead>
            <tbody>
              {opnames.map(o => (
                <tr key={o.opname}>
                  <td>{o.woord}</td>
                  <td>{o.bedoeld}</td>
                  <td>{o.spreker || ''}</td>
                  <td className="text-small">{o.model}</td>
                  {gegevens.condities.map(c => {
                    const u = o.condities[c];
                    return <td key={c}>{!u ? '' : u.fout ? 'fout van het model' : <>{u.gezegd || '(niets)'} · {label(u)}</>}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
